import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  use,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useSWRConfig } from 'swr';
import Splash from 'components/loading/Splash';
import { Sentry } from 'lib/sentry';
import { firebaseAuth } from 'services/firebase/firebase';
import { clearTokens, resetLoggedOutFlag } from 'utils/api-client';
import { rememberLastRole } from 'utils/post-logout';
import { setSessionFlagCookie, clearSessionFlagCookie } from 'utils/sessionFlagCookie';
import * as sessionStore from 'utils/sessionStore';
import { weddingPaths } from 'routes/paths';
import { User } from 'types/users';

// Wedding app uses adapter layer to map API responses to Aurora User format
// See: services/api/adapters.ts

interface AuthJwtContextInterface {
  sessionUser: User | null;
  setSessionUser: Dispatch<SetStateAction<User | null>>;
  setSession: (user: User | null, token?: string, refreshToken?: string) => void;
  refreshSessionUser: () => Promise<User | null>;
  signout: () => void;
  /**
   * Enter a "Try the full plan" sandbox on THIS tab. Stores the demo session in
   * per-tab sessionStorage — the real localStorage session is never touched.
   */
  enterSandbox: (user: User, token: string, refreshToken: string) => void;
  /**
   * Leave the sandbox and re-hydrate the untouched real session (or clear it).
   * Hard-reloads into `destination` (defaults to the real dashboard, or the login
   * page for an anonymous visitor) so no demo-scoped UI state survives the exit.
   */
  exitSandbox: (destination?: string) => void;
}

export const AuthJwtContext = createContext({} as AuthJwtContextInterface);

const AuthJwtProvider = ({ children }: PropsWithChildren) => {
  const [sessionUser, setSessionUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { mutate } = useSWRConfig();

  // Note: Aurora's useGetCurrentUser hook is disabled for wedding app
  // Wedding app uses custom authentication flow with loginCouple/joinEvent
  // const { data } = useGetCurrentUser();

  const setSession = useCallback(
    (user: User | null, token?: string, refreshToken?: string) => {
      setSessionUser(user);
      // Scope-aware: on a demo tab this reads/writes the sandbox sessionStorage
      // scope; otherwise the real localStorage keys (unchanged behaviour).
      const sandbox = sessionStore.isSandboxActive();
      if (user) {
        resetLoggedOutFlag();
        sessionStore.setStoredUserRaw(JSON.stringify(user));
        // The role hint + shared-domain presence cookie describe the REAL
        // session only — don't overwrite/set them from inside a demo.
        if (!sandbox) {
          rememberLastRole(user.role, user.eventId ?? null);
          setSessionFlagCookie();
        }
      } else {
        sessionStore.removeStoredUser();
        if (!sandbox) clearSessionFlagCookie();
      }
      if (token || refreshToken) {
        sessionStore.setTokens(token, refreshToken);
      }
    },
    [setSessionUser],
  );

  const refreshSessionUser = useCallback(async (): Promise<User | null> => {
    const storedUser = sessionStore.getStoredUserRaw();
    if (!storedUser) return null;

    try {
      const user = JSON.parse(storedUser);
      if (user.role === 'couple') {
        user.uploaderId = 'couple';
      }
      if (user.role !== 'couple') return user;

      const { getCoupleData } = await import('services/api/couple');
      const coupleData = await getCoupleData();

      const updatedUser = {
        ...user, // preserves isSandbox for a demo session
        plan: coupleData.event.plan,
        eventType: coupleData.event.eventType ?? 'wedding',
        subscriptionStatus: coupleData.event.subscriptionStatus,
        validUntil: coupleData.event.validUntil,
        storageUsed: coupleData.event.usedBytes,
        storageLimit: coupleData.event.maxBytes,
        photoCap: coupleData.photoCap,
        videoUploads: coupleData.planLimits?.videoUploads,
        weddingDate: coupleData.event.dateISO,
        hasQuiz: coupleData.event.hasQuiz ?? false,
        isEmailVerified: coupleData.couple.isEmailVerified ?? user.isEmailVerified ?? false,
      };

      setSessionUser(updatedUser);
      sessionStore.setStoredUserRaw(JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Failed to refresh session user:', error);
      return null;
    }
  }, [setSessionUser]);

  // ── Sandbox ("Try the full plan") entry/exit ─────────────────────────────
  // The demo lives entirely in per-tab sessionStorage; the real localStorage
  // session is never mutated, so there is nothing to stash and nothing to
  // restore — exit simply drops the sandbox scope and re-reads the real one.
  const exitSandbox = useCallback(
    (destination?: string) => {
      sessionStore.exitSandboxStorage();
      const raw = sessionStore.getRealStoredUserRaw();
      if (raw) {
        try {
          const u = JSON.parse(raw);
          if (u.role === 'couple') u.uploaderId = 'couple';
          setSessionUser(u);
        } catch {
          setSessionUser(null);
        }
      } else {
        setSessionUser(null);
      }
      // Drop any demo data cached by SWR so the restored session refetches clean.
      mutate(() => true, undefined, { revalidate: false });
      // ...but restoring session state in place isn't enough on its own: pages
      // that fetch their data into local component state (e.g. the couple
      // dashboard) don't refetch on a client-side navigate — and don't even
      // remount when we're already on the destination route — so they'd keep
      // rendering demo content over the real session. A full document reload
      // rebuilds every surface from localStorage. That's the same path a normal
      // app boot takes, so it's well-exercised, and it mirrors resetSandbox's
      // navigate(0).
      const dest =
        destination ??
        (sessionStore.hasRealSession() ? weddingPaths.coupleDashboard : weddingPaths.coupleLogin);
      window.location.assign(dest);
    },
    [setSessionUser, mutate],
  );

  const enterSandbox = useCallback(
    (user: User, token: string, refreshToken: string) => {
      // Set the active flag + store the demo session (per-tab). Never touches
      // localStorage, so other tabs and the real session are unaffected.
      sessionStore.enterSandboxStorage(token, refreshToken, JSON.stringify(user));
      resetLoggedOutFlag();
      setSessionUser(user);
      // Hard-reload into the dashboard (symmetric with exitSandbox): the "Try the
      // full plan" button can live on the dashboard itself (the free/Bouquet
      // upsell), and a same-route client-side navigate wouldn't remount it — it
      // would render the real couple's data under demo chrome. Reloading into the
      // sandbox scope is the same path a mid-demo browser refresh takes
      // (sessionStorage survives the reload), so the demo boots clean.
      window.location.assign(weddingPaths.coupleDashboard);
    },
    [setSessionUser],
  );

  const signout = useCallback(() => {
    // Inside a demo, "log out" means leave the demo — never touch the real
    // session or the shared-domain cookie.
    if (sessionStore.isSandboxActive()) {
      exitSandbox();
      return;
    }
    setSessionUser(null);
    clearTokens();
    clearSessionFlagCookie();
    // Clear all SWR cached data so a subsequent login doesn't see stale entries
    mutate(() => true, undefined, { revalidate: false });
    if (sessionUser?.provider === 'firebase' && firebaseAuth) {
      firebaseAuth.signOut();
    }
  }, [setSessionUser, sessionUser, mutate, exitSandbox]);

  // Initialize session from localStorage on mount.
  // We hydrate the user synchronously from localStorage so the app can render
  // immediately, then refresh couple data in the background. This avoids a
  // multi-second white screen on slow connections. The tradeoff is that plan/
  // storage values may be momentarily stale until the background refresh lands.
  useEffect(() => {
    // Scope-aware hydration: on a demo tab (sandbox_active set, survives reload)
    // this reads the sandbox scope and the demo continues; otherwise the real
    // localStorage session, exactly as before.
    const sandbox = sessionStore.isSandboxActive();
    const storedUser = sessionStore.getStoredUserRaw();
    const token = sessionStore.getAccessToken();
    const refreshToken = sessionStore.getRefreshToken();

    if (storedUser && (token || refreshToken)) {
      try {
        const user = JSON.parse(storedUser);
        if (user.role === 'couple') {
          user.uploaderId = 'couple';
        }
        setSessionUser(user);
        // Role hint + presence cookie describe the REAL session only.
        if (!sandbox) {
          rememberLastRole(user.role, user.eventId ?? null);
          setSessionFlagCookie();
        }

        if (user.role === 'couple') {
          (async () => {
            try {
              const { getCoupleData } = await import('services/api/couple');
              const coupleData = await getCoupleData();
              const refreshed = {
                ...user, // preserves isSandbox for a demo session
                plan: coupleData.event.plan,
                eventType: coupleData.event.eventType ?? 'wedding',
                storageUsed: coupleData.event.usedBytes,
                storageLimit: coupleData.event.maxBytes,
                photoCap: coupleData.photoCap,
                videoUploads: coupleData.planLimits?.videoUploads,
                weddingDate: coupleData.event.dateISO,
                hasQuiz: coupleData.event.hasQuiz ?? false,
                isEmailVerified: coupleData.couple.isEmailVerified ?? user.isEmailVerified ?? false,
              };
              setSessionUser(refreshed);
              sessionStore.setStoredUserRaw(JSON.stringify(refreshed));
            } catch (error) {
              console.error('Failed to refresh couple data:', error);
            }
          })();
        }
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        if (sandbox) {
          sessionStore.exitSandboxStorage();
        } else {
          sessionStore.clearRealSession();
          clearSessionFlagCookie();
        }
      }
    }
    setIsInitialized(true);
  }, []);

  // Keep Sentry user context in sync with the active session.
  useEffect(() => {
    if (sessionUser) {
      Sentry.setUser({
        id: String(sessionUser.id),
        email: sessionUser.email,
        role: sessionUser.role,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [sessionUser]);

  if (!isInitialized) {
    return <Splash />;
  }

  return (
    <AuthJwtContext.Provider
      value={{
        sessionUser,
        setSessionUser,
        setSession,
        refreshSessionUser,
        signout,
        enterSandbox,
        exitSandbox,
      }}
    >
      {children}
    </AuthJwtContext.Provider>
  );
};

export const useAuth = () => use(AuthJwtContext);

export const demoUser: User = {
  id: 'demo-couple-id',
  name: 'Demo Couple',
  email: 'demo@wedding.com',
  avatar: '/assets/images/avatar/default-couple.png',
  status: 'active',
  role: 'couple',
  coupleId: 'demo-couple-id',
  eventId: 'demo-event-id',
};

export default AuthJwtProvider;
